import React from 'react'
import CardButton from 'blaze-ui/CardButton/CardButton'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { MultiStepStepProps } from 'components/MultiStepFlow/Types'
import Styles from './SelectionStep.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useSelectionHandler } from '../hooks/useSelectionHandler'

export type SelectionOption<T> = {
  title: string
  value: T
  description?: string
  icon?: JSX.Element
  image?: string
  subLabel?: string
}

type SelectionStepProps<T> = {
  title: string
  description?: string
  options: SelectionOption<T>[]
  paramName: string
  dataKey: string
  variant?: 'horizontal' | 'vertical' | 'square'
  maxContentWidth?: string
  renderMedia?: (option: SelectionOption<T>) => React.ReactNode
  generateEvent?: (value: string) => any
  columnsCount?: number
  cardLabelSize?: 'sm' | 'md'
  isNextButtonDisabled?: boolean
} & MultiStepStepProps

export const SelectionStep = <T extends string>({
  title,
  description,
  options,
  onNext,
  onSkip,
  onBack,
  paramName,
  dataKey,
  variant = 'horizontal',
  maxContentWidth,
  renderMedia,
  generateEvent,
  columnsCount,
  cardLabelSize,
  isNextButtonDisabled,
}: SelectionStepProps<T>) => {
  const { selectedValue, handleSelection, handleNext } = useSelectionHandler({
    paramName,
    dataKey,
    onNext,
    generateEvent,
  })

  const { isMobile } = useResponsiveDesign()
  let defaultColumnsCount = columnsCount || 3

  if (isMobile) {
    defaultColumnsCount = 2
  }

  // Default media renderer if custom one isn't provided
  const defaultRenderMedia = (option: SelectionOption<T>) => {
    if (option.icon) {
      return option.icon
    }

    if (option.image) {
      return <img src={option.image} alt={option.title} />
    }

    return null
  }

  // Use custom media renderer if provided, otherwise use default
  const mediaRenderer = renderMedia || defaultRenderMedia

  return (
    <MultiStepStepLayout
      title={title}
      description={description}
      onNext={isMobile ? undefined : handleNext}
      onBack={isMobile ? undefined : onBack}
      onSkip={onSkip}
      isNextButtonDisabled={!selectedValue || isNextButtonDisabled}
    >
      <div className={Styles.options} style={{ gridTemplateColumns: `repeat(${defaultColumnsCount}, 1fr)` }}>
        {options.map((option) => (
          <CardButton
            key={option.value}
            label={option.title}
            subLabel={option.description || option.subLabel || ''}
            variant={variant}
            labelSize={cardLabelSize}
            isSelected={selectedValue === option.value}
            onPress={() => handleSelection(option.value)}
            icon={option.icon}
            maxContentWidth={maxContentWidth}
            media={mediaRenderer(option)}
          />
        ))}
      </div>
    </MultiStepStepLayout>
  )
}

export default SelectionStep
